/* Copyright (c) 2020-present CloudIO Inc */

import { getCustomChildItemTypes } from '../../registry/Manager';
import { checkForFixedChild } from '../../utils/ManagerUtils';
import { Platform } from '../../platform/Platform';
import {
  AliasType,
  ComponentManager,
  ComponentManagerFn,
} from '../../registry/Component.type';
import { ItemType } from '../../registry/ComponentRegistry';
import { baseComponentManager } from '../../registry/Components';
import { defaultValue } from './DataStoreReference';

const name = 'Data Store Reference';

const DataStoreReferenceManager: ComponentManagerFn<'DataStoreReference'> = (
  platform: Platform,
): ComponentManager<'DataStoreReference'> => {
  return {
    ...baseComponentManager<'DataStoreReference'>(
      platform,
      'DataStoreReference',
    ),
    defaultValue,
    isChildAllowed: (page, item, childType, context) =>
      (
        [
          ...getCustomChildItemTypes('DataStoreReference'),
          'Avatar',
          'BingMap',
          'Box',
          'BoxContainer',
          'BoxList',
          'CanvasGrid',
          'Card',
          'Code',
          'D3Chart',
          'DataGrid',
          'DataList',
          'ExpressionWatchListener',
          'FlexPanel',
          'Form',
          'FormSearch',
          'HorizontalSplitPanel',
          'Mirror',
          'Paper',
          'PivotTable',
          'ProgressIndicator',
          'RowPanel',
          'SearchForm',
          'SimpleFilter',
          'SmartSearch',
          'TabPanel',
          'Timeline',
          'Toolbar',
          'UserList',
          'UserPic',
          'VerticalSplitPanel',
        ] as ItemType[]
      ).includes(childType) &&
      checkForFixedChild(platform, page, item, childType, name, context),
    props: {
      alias: {
        label: 'Alias',
        type: 'DataSourceAliasChooser',
        required: true,
        allowedTypes: [
          'CloudDataStore',
          'DataStore',
          'LocalDataStore',
          'ReadOnlyDataStore',
          'TransientDataStore',
        ],
      },
    },
    category: 'Data',
    name,
    canHaveChildren: () => true,
    getDataSourceAliasProperty: (type: AliasType) =>
      type === 'STORE' ? { aliasProp: 'alias' } : undefined,
    isDataSourceAliasUsed: (item, alias) => item.props.alias === alias,
    getDisplayText: (item, expanded) => {
      return item.props.alias;
    },
    generateShortId: (item) => item.alias,
    layout: 'sizeAware',
    icon: { iconName: 'database' },
    description: `**Overview**<br/>
The Data Store Reference is a structural component that acts as a "binding scope" for your UI. It connects a specific section of your page layout to an existing Data Store.
While the Data Store manages the actual data lifecycle (fetching, saving, permissions) at the page level, the Data Store Reference distributes that data to the visual components. Think of it as a "lens" or "pointer"—you can have multiple references pointing to the same Data Store, allowing you to display the same dataset in different ways across the page without duplicating logic.

**1. Configuration**<br/>
This component is intentionally simple to configure because its only job is to establish a connection.
* **Alias:** Select the Data Source Alias of the Data Store you want to use.
* **Requirement:** The Data Store must already be defined on the Page (e.g., "employeeList").
* **Effect:** All child components placed inside this reference will automatically inherit this data context.

**2. Content & Composition**<br/>
The Data Store Reference creates a boundary for data. Any component placed inside it "knows" which data to use.
* **Allowed Children:** You typically place data-consuming components inside this reference:
    * **Visualization:** Data Grid, Chart, Pivot Table, Timeline.
    * **Input:** Form, Search Form.
    * **Layout:** Flex Panel, Tab Panel, Split Panel (to organize complex views of the data).

**3. Strategic Benefits: Reuse & Scoping**<br/>
Using Data Store References is the key to clean, scalable application architecture.
* **Single Source of Truth:** You define the Data Store once (e.g., "Sales Data"). You can then create multiple References to it (e.g., one for a Chart showing trends, another for a Grid showing details). Both views stay perfectly in sync.
* **Layout Organization:** Instead of configuring the Datasource Alias property on 50 individual form fields, you simply wrap the entire Form in a Data Store Reference. The fields automatically bind to the correct store, reducing configuration errors.

**4. Mental Model**<br/>
* **Data Store:** The "Brain." It owns the data, handles API calls, and manages state.
* **Data Store Reference:** The "Eyes." It looks at the Data Store and exposes that data to the UI components inside it.`,
  };
};

export default DataStoreReferenceManager;
